import { Link, usePage } from '@inertiajs/react';
import { useState, useEffect, useContext, Fragment } from 'react';
import { Menu, Transition, Dialog, Disclosure } from '@headlessui/react';
import ThemeToggle from '@/Components/ThemeToggle';
import { ModalContext } from '@/Layouts/MainLayout';
import { formatCredits } from '@/lib/formatCredits';

export default function Header() {
    const { auth, settings, menus } = usePage().props;
    const { openLogin } = useContext(ModalContext);
    const [isScrolled, setIsScrolled] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 50);
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const scrollToSection = (e, targetId) => {
        e.preventDefault();
        if (!targetId || targetId === '#') return;

        try {
            const element = document.querySelector(targetId);
            if (element) {
                const headerOffset = 80; // Height of sticky header
                const elementPosition = element.getBoundingClientRect().top;
                const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

                window.scrollTo({
                    top: offsetPosition,
                    behavior: 'smooth'
                });
            }
        } catch (error) {
            console.warn('Invalid scroll selector:', targetId);
        }
    };

    return (
        <header className={`fixed top-0 left-0 right-0 z-50 flex items-center justify-between gap-4 px-6 lg:px-12 transition-all duration-300 backdrop-blur-md ${isScrolled
            ? 'bg-white/95 dark:bg-background-dark/95 border-b border-slate-200 dark:border-white/10 shadow-2xl shadow-black/5 dark:shadow-black/50 py-3'
            : 'bg-white/60 dark:bg-background-dark/60 border-b border-transparent py-5'
            }`}>
            <Link href="/" className="group flex shrink-0 items-center gap-2">
                {settings?.logo ? (
                    <img
                        src={settings.logo}
                        alt={settings.name}
                        loading="eager"
                        fetchPriority="high"
                        className="h-10 w-auto max-w-[180px] shrink-0 object-contain transition-transform duration-300 group-hover:scale-110"
                    />
                ) : (
                    <>
                        <div className="size-7 text-primary transition-transform group-hover:scale-110 group-hover:rotate-12 duration-300">
                            <svg fill="none" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
                                <path clipRule="evenodd" d="M24 18.4228L42 11.475V34.3663C42 34.7796 41.7457 35.1504 41.3601 35.2992L24 42V18.4228Z" fill="currentColor" fillRule="evenodd"></path>
                                <path clipRule="evenodd" d="M24 8.18819L33.4123 11.574L24 15.2071L14.5877 11.574L24 8.18819ZM9 15.8487L21 20.4805V37.6263L9 32.9945V15.8487ZM27 37.6263V20.4805L39 15.8487V32.9945L27 37.6263ZM25.354 2.29885C24.4788 1.98402 23.5212 1.98402 22.646 2.29885L4.98454 8.65208C3.7939 9.08038 3 10.2097 3 11.475V34.3663C3 36.0196 4.01719 37.5026 5.55962 38.098L22.9197 44.7987C23.6149 45.0671 24.3851 45.0671 25.0803 44.7987L42.4404 38.098C43.9828 37.5026 45 36.0196 45 34.3663V11.475C45 10.2097 44.2061 9.08038 43.0155 8.65208L25.354 2.29885Z" fill="currentColor" fillRule="evenodd"></path>
                            </svg>
                        </div>
                        <h1 className="whitespace-nowrap text-2xl font-bold tracking-tight text-slate-900 transition-colors group-hover:text-primary dark:text-white">{settings?.name || import.meta.env.VITE_MASTER_TENANT_NAME}</h1>
                    </>
                )}
            </Link>
            <nav className="hidden min-w-0 flex-1 items-center justify-center gap-5 px-4 lg:flex xl:gap-7">
                {menus?.length > 0 ? (
                    menus.map((menu) => (
                        <div key={menu.id} className="relative shrink-0 group">
                            <Link
                                href={menu.url || '#'}
                                onClick={(e) => menu.url?.startsWith('#') && scrollToSection(e, menu.url)}
                                className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap text-sm font-semibold text-slate-600 transition-all hover:text-primary dark:text-white/70 dark:hover:text-primary"
                            >
                                {menu.name}
                                {menu.children?.length > 0 && <span className="material-symbols-outlined text-[16px]">expand_more</span>}
                                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-primary transition-all group-hover:w-full"></span>
                            </Link>

                            {/* Dropdown for children */}
                            {menu.children?.length > 0 && (
                                <div className="absolute top-full left-0 mt-2 w-56 bg-white dark:bg-background-dark border border-slate-200 dark:border-white/10 rounded-xl shadow-xl overflow-hidden opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 transform origin-top-left z-50">
                                    {menu.children.map((child) => (
                                        <Link
                                            key={child.id}
                                            href={child.url}
                                            onClick={(e) => child.url?.startsWith('#') && scrollToSection(e, child.url)}
                                            className="block px-5 py-3 text-sm text-slate-600 dark:text-white/70 hover:bg-slate-50 dark:hover:bg-white/5 hover:text-primary transition-colors"
                                        >
                                            {child.name}
                                        </Link>
                                    ))}
                                </div>
                            )}
                        </div>
                    ))
                ) : (
                    // Default fallback links if no menus are configured
                    <>
                        <a
                            onClick={(e) => scrollToSection(e, '#images')}
                            className="group relative inline-flex shrink-0 cursor-pointer items-center whitespace-nowrap text-sm font-semibold text-slate-600 transition-all hover:scale-110 hover:text-primary dark:text-white/70 dark:hover:text-primary"
                            href="/#images"
                        >
                            Images
                            <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-primary transition-all group-hover:w-full"></span>
                        </a>
                        <a
                            onClick={(e) => scrollToSection(e, '#videos')}
                            className="group relative inline-flex shrink-0 cursor-pointer items-center whitespace-nowrap text-sm font-semibold text-slate-600 transition-all hover:scale-110 hover:text-primary dark:text-white/70 dark:hover:text-primary"
                            href="/#videos"
                        >
                            Videos
                            <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-primary transition-all group-hover:w-full"></span>
                        </a>
                        <a
                            onClick={(e) => scrollToSection(e, '#chat')}
                            className="group relative inline-flex shrink-0 cursor-pointer items-center whitespace-nowrap text-sm font-semibold text-slate-600 transition-all hover:scale-110 hover:text-primary dark:text-white/70 dark:hover:text-primary"
                            href="/#chat"
                        >
                            Chat
                            <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-primary transition-all group-hover:w-full"></span>
                        </a>
                        <a
                            onClick={(e) => scrollToSection(e, '#voice')}
                            className="group relative inline-flex shrink-0 cursor-pointer items-center whitespace-nowrap text-sm font-semibold text-slate-600 transition-all hover:scale-110 hover:text-primary dark:text-white/70 dark:hover:text-primary"
                            href="/#voice"
                        >
                            Voice
                            <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-primary transition-all group-hover:w-full"></span>
                        </a>
                    </>
                )}
            </nav>

            <div className="flex shrink-0 items-center gap-3 lg:gap-4">
                {auth?.user && (
                    <div className="hidden items-center gap-1.5 whitespace-nowrap rounded-full border border-slate-200 bg-slate-100 px-3 py-1.5 dark:border-white/10 dark:bg-white/5 lg:flex">
                        <span className="material-symbols-outlined text-primary text-[20px]">token</span>
                        <span className="font-bold text-slate-700 dark:text-white text-sm">{formatCredits(auth.user.credits || 0)}</span>
                    </div>
                )}
                <ThemeToggle />
                {auth?.user ? (
                    <Menu as="div" className="relative hidden lg:block">
                        <Menu.Button className="size-10 rounded-full bg-primary flex items-center justify-center text-black font-bold hover:bg-primary/90 transition-all hover:scale-105 overflow-hidden border border-primary/50 shadow-sm">
                            {auth.user.avatar ? (
                                <img src={auth.user.avatar} alt={auth.user.name} className="w-full h-full object-cover" referrerPolicy="no-referrer" />
                            ) : (
                                auth.user.name?.charAt(0).toUpperCase()
                            )}
                        </Menu.Button>
                        <Transition
                            as={Fragment}
                            enter="transition ease-out duration-100"
                            enterFrom="transform opacity-0 scale-95"
                            enterTo="transform opacity-100 scale-100"
                            leave="transition ease-in duration-75"
                            leaveFrom="transform opacity-100 scale-100"
                            leaveTo="transform opacity-0 scale-95"
                        >
                            <Menu.Items modal={false} className="absolute right-0 mt-2 w-64 origin-top-right rounded-2xl bg-white dark:bg-background-dark border border-slate-200 dark:border-white/10 shadow-2xl focus:outline-none overflow-hidden backdrop-blur-xl">
                                <div className="px-4 py-4 border-b border-slate-200 dark:border-white/10 flex flex-col items-center gap-3">
                                    <div className="size-16 rounded-full bg-primary flex items-center justify-center text-black text-2xl font-bold shadow-lg overflow-hidden border-2 border-white dark:border-slate-800">
                                        {auth.user.avatar ? (
                                            <img src={auth.user.avatar} alt={auth.user.name} className="w-full h-full object-cover" referrerPolicy="no-referrer" />
                                        ) : (
                                            auth.user.name?.charAt(0).toUpperCase()
                                        )}
                                    </div>
                                    <div className="text-center w-full">
                                        <p className="text-base font-bold text-slate-900 dark:text-white truncate">{auth.user.name}</p>
                                        <p className="text-xs text-slate-500 dark:text-white/50 truncate">{auth.user.email}</p>
                                        {auth.user?.credit_expires_at && (() => {
                                            const daysLeft = Math.ceil((new Date(auth.user.credit_expires_at) - Date.now()) / 86400000);
                                            if (daysLeft <= 0) return (
                                                <p className="mt-2 text-[11px] font-semibold px-2 py-1 rounded-full bg-red-500/10 text-red-500 inline-flex items-center gap-1">
                                                    <span className="material-symbols-outlined text-[13px]">warning</span>
                                                    Credits expired!
                                                </p>
                                            );
                                            const color = daysLeft <= 7 ? 'bg-red-500/10 text-red-500' : daysLeft <= 30 ? 'bg-orange-500/10 text-orange-500' : 'bg-amber-500/10 text-amber-500';
                                            return (
                                                <p className={`mt-2 text-[11px] font-semibold px-2 py-1 rounded-full inline-flex items-center gap-1 ${color}`}>
                                                    <span className="material-symbols-outlined text-[13px]">schedule</span>
                                                    Credits expire in {daysLeft}d
                                                </p>
                                            );
                                        })()}
                                    </div>
                                </div>
                                <div className="py-1">
                                    <Menu.Item>
                                        {({ active }) => (
                                            <Link
                                                href={route('manager.dashboard')}
                                                className={`${active ? 'bg-slate-50 dark:bg-white/5 text-primary' : 'text-slate-600 dark:text-white/70'
                                                    } group flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors`}
                                            >
                                                <span className="material-symbols-outlined text-[20px]">dashboard</span>
                                                Dashboard
                                            </Link>
                                        )}
                                    </Menu.Item>
                                    <Menu.Item>
                                        {({ active }) => (
                                            <Link
                                                href={route('manager.profile.edit')}
                                                className={`${active ? 'bg-slate-50 dark:bg-white/5 text-primary' : 'text-slate-600 dark:text-white/70'
                                                    } group flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors`}
                                            >
                                                <span className="material-symbols-outlined text-[20px]">person</span>
                                                Profile
                                            </Link>
                                        )}
                                    </Menu.Item>
                                    <Menu.Item>
                                        {({ active }) => (
                                            <Link
                                                href={route('gallery.index')}
                                                className={`${active ? 'bg-slate-50 dark:bg-white/5 text-primary' : 'text-slate-600 dark:text-white/70'
                                                    } group flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors`}
                                            >
                                                <span className="material-symbols-outlined text-[20px]">grid_view</span>
                                                My Gallery
                                            </Link>
                                        )}
                                    </Menu.Item>
                                    <Menu.Item>
                                        {({ active }) => (
                                            <Link
                                                href={route('manager.profile.edit')}
                                                className={`${active ? 'bg-slate-50 dark:bg-white/5 text-primary' : 'text-slate-600 dark:text-white/70'
                                                    } group flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors w-full text-left`}
                                            >
                                                <span className="material-symbols-outlined text-[20px]">key</span>
                                                API Keys
                                            </Link>
                                        )}
                                    </Menu.Item>
                                </div>
                                <div className="py-1 border-t border-slate-200 dark:border-white/10">
                                    <Menu.Item>
                                        {({ active }) => (
                                            <Link
                                                href={route('logout')}
                                                method="post"
                                                as="button"
                                                className={`${active ? 'bg-red-50 dark:bg-red-500/10 text-red-600 dark:text-red-400' : 'text-slate-600 dark:text-white/70'
                                                    } group flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors w-full text-left`}
                                            >
                                                <span className="material-symbols-outlined text-[20px]">logout</span>
                                                Logout
                                            </Link>
                                        )}
                                    </Menu.Item>
                                </div>
                            </Menu.Items>
                        </Transition>
                    </Menu>
                ) : (
                    <button
                        onClick={openLogin}
                        className="hidden lg:block bg-primary hover:bg-primary/90 text-black px-7 py-2.5 rounded-full text-sm font-bold transition-all hover:scale-105 active:scale-95"
                    >
                        Sign In
                    </button>
                )}

                {/* Mobile Menu Button */}
                <button
                    onClick={() => setIsMobileMenuOpen(true)}
                    className="lg:hidden size-10 flex items-center justify-center rounded-full text-slate-700 dark:text-white hover:bg-slate-100 dark:hover:bg-white/5 transition-colors"
                >
                    <span className="material-symbols-outlined text-2xl">menu</span>
                </button>
            </div>

            {/* Mobile Menu Drawer */}
            <Transition show={isMobileMenuOpen} as={Fragment}>
                <Dialog as="div" className="relative z-[60] lg:hidden" onClose={setIsMobileMenuOpen || (() => {})}>
                    <Transition.Child
                        as={Fragment}
                        enter="transition-opacity ease-linear duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="transition-opacity ease-linear duration-300"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm" />
                    </Transition.Child>

                    <div className="fixed inset-0 flex justify-end">
                        <Transition.Child
                            as={Fragment}
                            enter="transition ease-in-out duration-500 transform"
                            enterFrom="translate-x-full opacity-0"
                            enterTo="translate-x-0 opacity-100"
                            leave="transition ease-in-out duration-500 transform"
                            leaveFrom="translate-x-0 opacity-100"
                            leaveTo="translate-x-full opacity-0"
                        >
                            <Dialog.Panel dir="rtl" className="relative w-full max-w-xs bg-transparent flex flex-col h-full overflow-y-auto">
                                <div className="flex justify-start p-6">
                                    <button
                                        onClick={() => setIsMobileMenuOpen(false)}
                                        className="size-10 flex items-center justify-center rounded-full hover:bg-white/10 text-primary transition-colors"
                                    >
                                        <span className="material-symbols-outlined text-3xl">close</span>
                                    </button>
                                </div>

                                <div className="flex-1 px-6 py-2 overflow-y-auto no-scrollbar">
                                    <div className="flex flex-col gap-4">
                                        {menus?.length > 0 ? (
                                            menus.map(menu => (
                                                <Fragment key={menu.id}>
                                                    {menu.children?.length > 0 ? (
                                                        <Disclosure as="div" className="transition-all">
                                                            {({ open }) => (
                                                                <>
                                                                    <Disclosure.Button className="flex w-full items-center justify-start gap-3 py-2 text-primary font-bold transition-colors group">
                                                                        <span className="text-2xl group-hover:pr-2 transition-all">{menu.name}</span>
                                                                        <span className={`material-symbols-outlined text-xl transition-transform duration-200 ${open ? 'rotate-180' : ''}`}>
                                                                            expand_more
                                                                        </span>
                                                                    </Disclosure.Button>
                                                                    <Transition
                                                                        enter="transition duration-200 ease-out"
                                                                        enterFrom="transform scale-95 opacity-0"
                                                                        enterTo="transform scale-100 opacity-100"
                                                                        leave="transition duration-150 ease-out"
                                                                        leaveFrom="transform scale-100 opacity-100"
                                                                        leaveTo="transform scale-95 opacity-0"
                                                                    >
                                                                        <Disclosure.Panel className="pr-4 flex flex-col gap-2 pt-2">
                                                                            {menu.children.map(child => (
                                                                                <Link
                                                                                    key={child.id}
                                                                                    href={child.url}
                                                                                    className="py-2 text-lg font-medium text-primary/70 hover:text-primary transition-colors block border-r-2 border-primary/20 pr-4 hover:border-primary"
                                                                                    onClick={() => setIsMobileMenuOpen(false)}
                                                                                >
                                                                                    {child.name}
                                                                                </Link>
                                                                            ))}
                                                                        </Disclosure.Panel>
                                                                    </Transition>
                                                                </>
                                                            )}
                                                        </Disclosure>
                                                    ) : (
                                                        <Link
                                                            href={menu.url || '#'}
                                                            className="py-2 text-primary font-bold transition-colors block text-2xl hover:pr-2 transition-all"
                                                            onClick={() => setIsMobileMenuOpen(false)}
                                                        >
                                                            {menu.name}
                                                        </Link>
                                                    )}
                                                </Fragment>
                                            ))
                                        ) : (
                                            <>
                                                <a href="/#images" onClick={() => setIsMobileMenuOpen(false)} className="py-2 text-primary font-bold transition-colors block text-2xl hover:pr-2 transition-all">Images</a>
                                                <a href="/#videos" onClick={() => setIsMobileMenuOpen(false)} className="py-2 text-primary font-bold transition-colors block text-2xl hover:pr-2 transition-all">Videos</a>
                                                <a href="/#chat" onClick={() => setIsMobileMenuOpen(false)} className="py-2 text-primary font-bold transition-colors block text-2xl hover:pr-2 transition-all">Chat</a>
                                                <a href="/#voice" onClick={() => setIsMobileMenuOpen(false)} className="py-2 text-primary font-bold transition-colors block text-2xl hover:pr-2 transition-all">Voice</a>
                                            </>
                                        )}
                                    </div>

                                    <div className="mt-auto pt-10 pb-10">
                                        {auth?.user ? (
                                            <Disclosure as="div">
                                                {({ open }) => (
                                                    <>
                                                        <Disclosure.Button className="w-full flex items-center justify-start gap-4 p-2 transition-colors group">
                                                            <div className="flex items-center gap-3 min-w-0 text-start">
                                                                <div className="size-12 rounded-full border-2 border-primary p-0.5">
                                                                    <div className="w-full h-full rounded-full bg-primary flex items-center justify-center text-black font-bold overflow-hidden">
                                                                        {auth.user.avatar ? (
                                                                            <img src={auth.user.avatar} alt={auth.user.name} className="w-full h-full object-cover" />
                                                                        ) : (
                                                                            auth.user.name?.charAt(0).toUpperCase()
                                                                        )}
                                                                    </div>
                                                                </div>
                                                                <div className="overflow-hidden">
                                                                    <div className="font-bold text-primary truncate text-lg group-hover:pr-1 transition-all">{auth.user.name}</div>
                                                                    <div className="flex items-center gap-1 text-sm font-bold text-primary/70">
                                                                        <span className="material-symbols-outlined text-[16px]">token</span>
                                                                        {formatCredits(auth.user.credits || 0)}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            <span className={`material-symbols-outlined text-primary transition-transform duration-200 ${open ? 'rotate-180' : ''}`}>
                                                                expand_more
                                                            </span>
                                                        </Disclosure.Button>

                                                        <Transition
                                                            enter="transition duration-100 ease-out"
                                                            enterFrom="transform scale-95 opacity-0"
                                                            enterTo="transform scale-100 opacity-100"
                                                            leave="transition duration-75 ease-out"
                                                            leaveFrom="transform scale-100 opacity-100"
                                                            leaveTo="transform scale-95 opacity-0"
                                                        >
                                                            <Disclosure.Panel className="mt-2 flex flex-col gap-1 pr-4">
                                                                <Link href={route('manager.dashboard')} onClick={() => setIsMobileMenuOpen(false)} className="flex items-center gap-3 py-2 text-lg text-primary/80 hover:text-primary transition-colors font-medium hover:translate-x-[-5px]">
                                                                    <span className="material-symbols-outlined text-[24px]">dashboard</span>
                                                                    Dashboard
                                                                </Link>
                                                                <Link href={route('manager.profile.edit')} onClick={() => setIsMobileMenuOpen(false)} className="flex items-center gap-3 py-2 text-lg text-primary/80 hover:text-primary transition-colors font-medium hover:translate-x-[-5px]">
                                                                    <span className="material-symbols-outlined text-[24px]">person</span>
                                                                    Profile
                                                                </Link>
                                                                <Link href={route('gallery.index')} onClick={() => setIsMobileMenuOpen(false)} className="flex items-center gap-3 py-2 text-lg text-primary/80 hover:text-primary transition-colors font-medium hover:translate-x-[-5px]">
                                                                    <span className="material-symbols-outlined text-[24px]">grid_view</span>
                                                                    My Gallery
                                                                </Link>
                                                                <Link href={route('logout')} method="post" as="button" className="w-full text-start flex items-center gap-3 py-2 text-lg text-red-500 hover:text-red-400 transition-colors font-medium hover:translate-x-[-5px]">
                                                                    <span className="material-symbols-outlined text-[24px]">logout</span>
                                                                    Logout
                                                                </Link>
                                                            </Disclosure.Panel>
                                                        </Transition>
                                                    </>
                                                )}
                                            </Disclosure>
                                        ) : (
                                            <button
                                                onClick={() => {
                                                    setIsMobileMenuOpen(false);
                                                    openLogin();
                                                }}
                                                className="w-full bg-primary text-black py-4 rounded-full font-bold transition-all hover:scale-105 shadow-[0_0_20px_rgba(var(--primary-rgb),0.3)] flex items-center justify-center gap-2 text-lg"
                                            >
                                                <span className="material-symbols-outlined">login</span>
                                                Sign In
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </Dialog>
            </Transition>
        </header>
    );
}
